import { Component, OnInit } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import DOMPurify from 'dompurify';
import {
  CHANGELOG_ENVIRONMENTS,
  ChangelogEnvironment,
  ContainerInfo,
  IotEdgeReleaseResponse,
  IotEdgeReleaseService,
  ReleaseChangelog,
  SiteReleasesResponse,
} from '../services/iotEdgeRelease.service';
import { Site, SiteService } from '../services/site.service';

/** Detect the MIME type of a base64-encoded image from its magic bytes. */
export function sniffImageMimeType(base64: string): string {
  if (base64.startsWith('iVBOR')) return 'image/png';
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/png';
}

function compareVersionsDesc(a: string, b: string): number {
  const pa = a.split('.').map((n) => parseInt(n, 10) || 0);
  const pb = b.split('.').map((n) => parseInt(n, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i++) {
    const diff = (pb[i] || 0) - (pa[i] || 0);
    if (diff !== 0) return diff;
  }
  return 0;
}

interface ReleaseWithSites extends IotEdgeReleaseResponse {
  assignedSites: { site: Site; isCurrent: boolean; isDesired: boolean }[];
}

@Component({
  selector: 'app-releases',
  templateUrl: './releases.component.html',
  styleUrls: ['./releases.component.css'],
  standalone: false,
})
export class ReleasesComponent implements OnInit {
  constructor(
    private releaseService: IotEdgeReleaseService,
    private siteService: SiteService,
    public snackBar: MatSnackBar,
    private sanitizer: DomSanitizer
  ) {}

  releases: ReleaseWithSites[] = [];
  sites: Site[] = [];
  siteReleasesMap: { [siteId: string]: SiteReleasesResponse } = {};

  syncing = false;
  selectedReleaseId: string | null = null;
  editingChangelog = false;
  savingChangelog = false;
  editorMode: 'source' | 'preview' = 'source';

  // Changelog editor form state
  clReleaseVersion = '';
  clHeading = '';
  clTitle = '';
  clHtmlContent = '';
  clLinkToManual = '';
  clEnvironments: ChangelogEnvironment[] = [];
  clImage: string | null = null; // base64 without data: prefix

  readonly environmentOptions = CHANGELOG_ENVIRONMENTS;
  readonly defaultManualLink = 'https://www.scanreach.com/support';

  ngOnInit(): void {
    this.loadData();
  }

  loadData(): void {
    this.releaseService.getAllReleases().subscribe(
      (releases) => {
        this.releases = releases
          .sort((a, b) => compareVersionsDesc(a.version, b.version))
          .map((r) => ({ ...r, assignedSites: [] }));

        this.siteService.getSites().subscribe(
          (sites) => {
            this.sites = sites;
            this.loadSiteAssignments(sites);
          },
          (err) => {
            console.error(err);
            this.snackBar.open('Error loading sites: ' + err.message, null, { duration: 8000 });
          }
        );
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error loading releases: ' + err.message, null, { duration: 8000 });
      }
    );
  }

  loadSiteAssignments(sites: Site[]): void {
    sites.forEach((site) => {
      this.releaseService.getSiteReleases(site.id).subscribe(
        (sr) => {
          this.siteReleasesMap[site.id] = sr;
          this.updateReleaseAssignments();
        },
        () => {}
      );
    });
  }

  updateReleaseAssignments(): void {
    this.releases.forEach((release) => {
      release.assignedSites = [];
    });

    Object.entries(this.siteReleasesMap).forEach(([siteId, sr]) => {
      const site = this.sites.find((s) => s.id === siteId);
      if (!site) return;

      if (sr.currentRelease) {
        const release = this.releases.find((r) => r.id === sr.currentRelease.id);
        if (release) {
          const existing = release.assignedSites.find((a) => a.site.id === siteId);
          if (existing) {
            existing.isCurrent = true;
          } else {
            release.assignedSites.push({ site, isCurrent: true, isDesired: false });
          }
        }
      }

      if (sr.desiredRelease) {
        const release = this.releases.find((r) => r.id === sr.desiredRelease.id);
        if (release) {
          const existing = release.assignedSites.find((a) => a.site.id === siteId);
          if (existing) {
            existing.isDesired = true;
          } else {
            release.assignedSites.push({ site, isCurrent: false, isDesired: true });
          }
        }
      }

      sr.availableReleases?.forEach((ar) => {
        const release = this.releases.find((r) => r.id === ar.id);
        if (release && !release.assignedSites.find((a) => a.site.id === siteId)) {
          release.assignedSites.push({ site, isCurrent: false, isDesired: false });
        }
      });
    });
  }

  syncReleases(): void {
    this.syncing = true;
    this.releaseService.syncReleases().subscribe(
      () => {
        this.snackBar.open('Releases synced from IoT Hub', null, { duration: 5000 });
        this.syncing = false;
        this.loadData();
      },
      (err) => {
        this.snackBar.open('Error syncing releases: ' + err.message, null, { duration: 8000 });
        this.syncing = false;
      }
    );
  }

  selectRelease(releaseId: string): void {
    this.selectedReleaseId = this.selectedReleaseId === releaseId ? null : releaseId;
    this.editingChangelog = false;
  }

  get selectedRelease(): ReleaseWithSites | null {
    return this.releases.find((r) => r.id === this.selectedReleaseId) || null;
  }

  isContainerChanged(release: ReleaseWithSites, container: ContainerInfo): boolean {
    const idx = this.releases.indexOf(release);
    const prevRelease = this.releases[idx + 1]; // releases is newest-first; idx+1 is the older release
    if (!prevRelease) return false;
    const prev = prevRelease.containers.find((c) => c.containerName === container.containerName);
    if (!prev) return true; // new container not present in previous release
    return prev.containerVersion !== container.containerVersion;
  }

  startEditChangelog(): void {
    const cl = this.selectedRelease?.changelog;
    this.clReleaseVersion = cl?.releaseVersion || this.selectedRelease?.version || '';
    this.clHeading = cl?.heading || '';
    this.clTitle = cl?.title || '';
    this.clHtmlContent = cl?.htmlContent || '';
    this.clLinkToManual = cl != null ? cl.linkToManual || '' : this.defaultManualLink;
    this.clEnvironments = cl?.environment ? [...cl.environment] : [];
    this.clImage = cl?.image || null;
    this.editingChangelog = true;
    this.editorMode = 'source';
  }

  cancelEditChangelog(): void {
    this.editingChangelog = false;
  }

  private isValidManualLink(link: string): boolean {
    if (!link) return true; // empty is allowed; backend only validates non-empty values
    try {
      const url = new URL(link);
      return url.protocol === 'http:' || url.protocol === 'https:';
    } catch {
      return false;
    }
  }

  saveChangelog(): void {
    if (!this.selectedReleaseId) return;

    if (!this.clTitle.trim()) {
      this.snackBar.open('Title is required', null, { duration: 5000 });
      return;
    }
    if (!this.clHtmlContent.trim()) {
      this.snackBar.open('HTML content is required', null, { duration: 5000 });
      return;
    }
    if (!this.isValidManualLink(this.clLinkToManual.trim())) {
      this.snackBar.open('Link to manual must be an absolute http(s) URL', null, { duration: 5000 });
      return;
    }

    const changelog: ReleaseChangelog = {
      releaseVersion: this.clReleaseVersion.trim() || this.selectedRelease?.version || '',
      heading: this.clHeading.trim() || null,
      title: this.clTitle.trim(),
      htmlContent: this.clHtmlContent.trim(),
      linkToManual: this.clLinkToManual.trim(),
      environment: this.clEnvironments,
      image: this.clImage,
    };

    this.savingChangelog = true;
    this.releaseService.setChangelog(this.selectedReleaseId, { changelog }).subscribe(
      () => {
        this.snackBar.open('Changelog saved', null, { duration: 5000 });
        this.savingChangelog = false;
        this.editingChangelog = false;
        // Optimistic local update, then reload from server to show what was actually persisted
        const release = this.releases.find((r) => r.id === this.selectedReleaseId);
        if (release) {
          release.changelog = changelog;
        }
        this.reloadSelectedRelease();
      },
      (err) => {
        this.snackBar.open('Error saving changelog: ' + (err.error?.message || err.message), null, {
          duration: 8000,
        });
        this.savingChangelog = false;
      }
    );
  }

  /** Re-fetches releases from the API and verifies the changelog round-trip, keeping the current selection. */
  private reloadSelectedRelease(): void {
    const selectedId = this.selectedReleaseId;
    this.releaseService.getAllReleases().subscribe(
      (releases) => {
        releases.forEach((fresh) => {
          const existing = this.releases.find((r) => r.id === fresh.id);
          if (existing) {
            existing.changelog = fresh.changelog;
            existing.releaseDate = fresh.releaseDate;
            existing.readySiteIds = fresh.readySiteIds;
          }
        });
        const selected = releases.find((r) => r.id === selectedId);
        if (selected && !selected.changelog) {
          this.snackBar.open(
            'Warning: the API did not return the changelog after saving — it was not persisted by the backend.',
            'OK',
            { duration: 15000 }
          );
        }
      },
      (err) => {
        console.error(err);
        this.snackBar.open('Error reloading releases after save: ' + err.message, null, { duration: 8000 });
      }
    );
  }

  removeChangelog(): void {
    if (!this.selectedReleaseId || !this.selectedRelease?.changelog) return;
    if (!confirm('Remove the changelog for this release? This also clears the stored image.')) return;

    this.savingChangelog = true;
    this.releaseService.setChangelog(this.selectedReleaseId, { changelog: null }).subscribe(
      () => {
        this.snackBar.open('Changelog removed', null, { duration: 5000 });
        this.savingChangelog = false;
        this.editingChangelog = false;
        const release = this.releases.find((r) => r.id === this.selectedReleaseId);
        if (release) {
          release.changelog = null;
        }
      },
      (err) => {
        this.snackBar.open('Error removing changelog: ' + (err.error?.message || err.message), null, {
          duration: 8000,
        });
        this.savingChangelog = false;
      }
    );
  }

  onImagePaste(event: ClipboardEvent): void {
    const items = event.clipboardData?.items;
    if (!items) return;

    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.startsWith('image/')) {
        event.preventDefault();
        const file = item.getAsFile();
        if (!file) return;
        const reader = new FileReader();
        reader.onload = (e) => {
          const base64 = e.target?.result as string;
          const imgTag = `<img src="${base64}" style="max-width: 100%;" />`;
          this.clHtmlContent += imgTag;
        };
        reader.readAsDataURL(file);
        break;
      }
    }
  }

  onImageFileSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const base64 = e.target?.result as string;
      const imgTag = `<img src="${base64}" style="max-width: 100%;" />`;
      this.clHtmlContent += imgTag;
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  onMainImageSelect(event: Event): void {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      // API expects raw base64 without the data:...;base64, prefix
      this.clImage = dataUrl.substring(dataUrl.indexOf(',') + 1);
    };
    reader.readAsDataURL(file);
    input.value = '';
  }

  removeMainImage(): void {
    this.clImage = null;
  }

  /** Editor preview of the main image currently in the form */
  get clImageDataUrl(): string | null {
    return this.clImage ? `data:${sniffImageMimeType(this.clImage)};base64,${this.clImage}` : null;
  }

  /** Renderable data URL for a stored changelog image */
  getChangelogImageUrl(changelog: ReleaseChangelog | null): string | null {
    return changelog?.image ? `data:${sniffImageMimeType(changelog.image)};base64,${changelog.image}` : null;
  }

  getEnvironmentLabel(env: ChangelogEnvironment | string): string {
    return this.environmentOptions.find((o) => o.value === env)?.label || env;
  }

  /**
   * Sanitizes the HTML with DOMPurify (removes scripts/event handlers while keeping
   * inline styles and data: image URIs used by changelogs), then marks the cleaned
   * result as trusted so Angular renders it as-is.
   */
  getSafeHtml(html: string): SafeHtml {
    const clean = DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
    return this.sanitizer.bypassSecurityTrustHtml(clean);
  }

  getChangelogExcerpt(changelog: ReleaseChangelog | null): string {
    if (!changelog) return '';
    const text = (changelog.htmlContent || '')
      .replace(/<[^>]*>/g, '')
      .replace(/&nbsp;/g, ' ')
      .trim();
    return text.length > 120 ? text.substring(0, 120) + '…' : text;
  }

  getCurrentCount(release: ReleaseWithSites): number {
    return release.assignedSites.filter((s) => s.isCurrent).length;
  }

  getDesiredCount(release: ReleaseWithSites): number {
    return release.assignedSites.filter((s) => s.isDesired).length;
  }

  getAvailableCount(release: ReleaseWithSites): number {
    return release.assignedSites.length;
  }
}
